import React from "react";
import { useTranslation } from "react-i18next";
import CategorySelect from "../../components/CategorySelect";
import { MainCategory } from "../../data/categories";
import { GearPal } from "../../types/Pal";
import GearButton from "../../ui/gearbutton";

type GearPalItemForm = {
  name: string;
  categoryId: string;
  externalLink?: string;
  categoryLabel?: string;
};

interface UserAddressFormFieldsProps {
  register: any;
  errors: any;
  touchedFields: any;
  isSubmitted: boolean;
  watch: any;
  setValue: any;
  trigger: any;
  isValid: boolean;
  isAdding: boolean;
  isUpdating: boolean;
  isEdit: boolean;
  palToEdit?: GearPal | null;
  onClose: () => void;
}

const UserAddressFormFields: React.FC<UserAddressFormFieldsProps> = ({
  register,
  errors,
  touchedFields,
  isSubmitted,
  watch,
  setValue,
  trigger,
  isValid,
  isAdding,
  isUpdating,
  isEdit,
  palToEdit,
  onClose,
}) => {
  const { t } = useTranslation();
  const items = (watch("items") as GearPalItemForm[]) || [];
  const debug = false;
  // Funzione helper per decidere se mostrare l'errore
  const shouldShowError = (fieldName: string) => {
    return isSubmitted || touchedFields[fieldName];
  };

  // Determina se mostrare la sezione items
  // Mostra solo se è in edit mode E ci sono items
  const shouldShowItems = isEdit && items.length > 0;

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <label className="flex flex-col gap-2">
          <span className="text-sm text-white/80">
            {t("forms.name")} <span className="text-red-400">*</span>
          </span>
          <input
            type="text"
            {...register("name")}
            placeholder={t("forms.name")}
            className={`rounded-xl bg-[#2C1A4A] border px-4 py-3 text-white placeholder-white/40 focus:outline-none ${
              errors.name && shouldShowError("name")
                ? "border-red-500/60 focus:border-red-500/80"
                : "border-white/10 focus:border-primary/60"
            }`}
          />
          {errors.name && shouldShowError("name") && (
            <p className="text-sm text-red-300">{errors.name.message}</p>
          )}
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-sm text-white/80">
            {t("forms.city")} <span className="text-red-400">*</span>
          </span>
          <input
            type="text"
            {...register("city")}
            placeholder={t("forms.city")}
            className={`rounded-xl bg-[#2C1A4A] border px-4 py-3 text-white placeholder-white/40 focus:outline-none ${
              errors.city && shouldShowError("city")
                ? "border-red-500/60 focus:border-red-500/80"
                : "border-white/10 focus:border-primary/60"
            }`}
          />
          {errors.city && shouldShowError("city") && (
            <p className="text-sm text-red-300">{errors.city.message}</p>
          )}
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-sm text-white/80">
            {t("forms.zipCode")} <span className="text-red-400">*</span>
          </span>
          <input
            type="text"
            {...register("zipcode")}
            placeholder={t("forms.zipCode")}
            className={`rounded-xl bg-[#2C1A4A] border px-4 py-3 text-white placeholder-white/40 focus:outline-none ${
              errors.zipcode && shouldShowError("zipcode")
                ? "border-red-500/60 focus:border-red-500/80"
                : "border-white/10 focus:border-primary/60"
            }`}
          />
          {errors.zipcode && shouldShowError("zipcode") && (
            <p className="text-sm text-red-300">{errors.zipcode.message}</p>
          )}
          <p className="text-sm text-white/60">
            {t(
              "forms.zipCodeHint",
              "Per motivi di sicurezza, mostriamo solo la zona del codice postale sulla mappa"
            )}
          </p>
        </label>

        <label className="flex flex-col gap-2">
          <span className="text-sm text-white/80">
            {t("forms.region")} <span className="text-red-400">*</span>
          </span>
          <input
            type="text"
            {...register("region")}
            placeholder={t("forms.region")}
            className={`rounded-xl bg-[#2C1A4A] border px-4 py-3 text-white placeholder-white/40 focus:outline-none ${
              errors.region && shouldShowError("region")
                ? "border-red-500/60 focus:border-red-500/80"
                : "border-white/10 focus:border-primary/60"
            }`}
          />
          {errors.region && shouldShowError("region") && (
            <p className="text-sm text-red-300">{errors.region.message}</p>
          )}
        </label>
      </div>

      {/* Gear Items - Rimosso da qui, gestito in UserGearItems */}

      {/* Messaggio di errore generale */}
      {!isValid && debug && (
        <div className="mt-6 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          <h4 className="font-medium mb-2">
            {t("forms.validationErrors", "Errori di validazione:")}
          </h4>
          <ul className="space-y-1">
            {errors.name && errors.name.message && (
              <li>• {errors.name.message}</li>
            )}
            {errors.zipcode && errors.zipcode.message && (
              <li>• {errors.zipcode.message}</li>
            )}
            {errors.city && errors.city.message && (
              <li>• {errors.city.message}</li>
            )}
            {errors.region && errors.region.message && (
              <li>• {errors.region.message}</li>
            )}
          </ul>
        </div>
      )}

      {/* Pulsanti di submit */}
      <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-3 rounded-xl border border-white/15 text-white/90 hover:text-white hover:border-white/40 transition"
        >
          {t("forms.cancel", "Annulla")}
        </button>
        <GearButton
          type="submit"
          disabled={!isValid || isAdding || isUpdating}
          className="rounded-xl"
        >
          {isAdding
            ? t("forms.submitting", "Invio in corso...")
            : isUpdating
            ? t("forms.updating", "Aggiornamento in corso...")
            : isEdit
            ? t("forms.saveChanges", "Salva Modifiche")
            : t("forms.submit", "Invia")}
        </GearButton>
      </div>
    </div>
  );
};

export default UserAddressFormFields;
